import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../../../shared/enums/request-status';
import {
  getCustomCallOutcomesRequest,
  getDefaultCallOutcomesRequest,
  getAllCallOutcomesRequest,
  createCallOutcomeRequest,
  updateCallOutcomeRequest,
  deleteCallOutcomeRequest,
} from './extra-actions';
import { PaginationQueryParams } from '../../../../shared/types/request';
import { constants } from '../../../../shared/enums/constants';
import { CallOutcome, CallOutcomeItem } from './types';

interface RequestState {
  status: RequestStatus;
  message: string;
  error: any;
}

interface CallOutcomeState {
  getCustomCallOutcomesRequest: RequestState;
  getDefaultCallOutcomesRequest: RequestState;
  getAllCallOutcomesRequest: RequestState;
  createCallOutcomeRequest: RequestState;
  updateCallOutcomeRequest: RequestState;
  deleteCallOutcomeRequest: RequestState;
  customOutcomes: CallOutcome[];
  defaultOutcomes: CallOutcome[];
  allOutcomes: CallOutcome[];
  isLoading: boolean;
  customOutcomePagination: {
    options: Required<PaginationQueryParams> & { search?: string };
    pagesCount: number;
    count: number;
  };
  defaultOutcomePagination: {
    options: Required<PaginationQueryParams> & { search?: string };
    pagesCount: number;
    count: number;
  };
}

const initialRequestState: RequestState = {
  status: RequestStatus.Ideal,
  message: null,
  error: null,
};

const initialState: CallOutcomeState = {
  getCustomCallOutcomesRequest: { ...initialRequestState },
  getDefaultCallOutcomesRequest: { ...initialRequestState },
  getAllCallOutcomesRequest: { ...initialRequestState },
  createCallOutcomeRequest: { ...initialRequestState },
  updateCallOutcomeRequest: { ...initialRequestState },
  deleteCallOutcomeRequest: { ...initialRequestState },
  customOutcomes: [],
  defaultOutcomes: [],
  allOutcomes: [],
  isLoading: false,
  customOutcomePagination: {
    options: {
      page: 1,
      limit: constants.DEFAULT_PAGE_SIZE,
    },
    count: 0,
    pagesCount: 0,
  },
  defaultOutcomePagination: {
    options: {
      page: 1,
      limit: constants.DEFAULT_PAGE_SIZE,
    },
    count: 0,
    pagesCount: 0,
  },
};

// Transform API response item to processed call outcome
const processCallOutcomeItem = (item: CallOutcomeItem): CallOutcome => ({
  ...item,
  processedSentiment: {
    sentiment: item.sentiment,
    bgColor: item.bgColor,
    textColor: item.textColor,
  },
  processedName: {
    name: item.name,
    sentiment: item.sentiment,
    isDefault: item.isDefault === 1,
    emoji: null,
  },
});

const callOutcomeSlice = createSlice({
  name: 'callOutcome',
  initialState,
  reducers: {
    resetCallOutcomeData: (state) => {
      state.customOutcomes = [];
      state.defaultOutcomes = [];
    },
  },
  extraReducers: (builder) => {
    // Get custom call outcomes with pagination
    builder.addCase(
      getCustomCallOutcomesRequest.pending,
      (state, action) => {
        state.getCustomCallOutcomesRequest.status = RequestStatus.Pending;
        state.getCustomCallOutcomesRequest.error = null;
        Object.keys(state.customOutcomePagination.options).forEach((key) => {
          if (action.meta.arg[key] !== undefined) {
            state.customOutcomePagination.options[key] = action.meta.arg[key];
          }
        });
        state.isLoading = true;
      },
    );
    builder.addCase(
      getCustomCallOutcomesRequest.fulfilled,
      (state, action) => {
        state.getCustomCallOutcomesRequest.status = RequestStatus.Succeeded;
        state.getCustomCallOutcomesRequest.message =
          action.payload.message;
        const outcomes = action.payload.payload.items.map(
          processCallOutcomeItem,
        );
        state.customOutcomes = outcomes;
        state.customOutcomePagination.options.page =
          action.payload.payload.meta.currentPage;
        state.customOutcomePagination.count =
          action.payload.payload.meta.totalItems;
        state.isLoading = false;
      },
    );
    builder.addCase(
      getCustomCallOutcomesRequest.rejected,
      (state, action) => {
        state.getCustomCallOutcomesRequest.status = RequestStatus.Failed;
        state.getCustomCallOutcomesRequest.error = action.payload;
        state.isLoading = false;
      },
    );

    // Get default call outcomes with pagination
    builder.addCase(
      getDefaultCallOutcomesRequest.pending,
      (state, action) => {
        state.getDefaultCallOutcomesRequest.status = RequestStatus.Pending;
        state.getDefaultCallOutcomesRequest.error = null;
        Object.keys(state.defaultOutcomePagination.options).forEach((key) => {
          if (action.meta.arg[key] !== undefined) {
            state.defaultOutcomePagination.options[key] = action.meta.arg[key];
          }
        });
        state.isLoading = true;
      },
    );
    builder.addCase(
      getDefaultCallOutcomesRequest.fulfilled,
      (state, action) => {
        state.getDefaultCallOutcomesRequest.status = RequestStatus.Succeeded;
        state.getDefaultCallOutcomesRequest.message =
          action.payload.message;
        const outcomes = action.payload.payload.items.map(
          processCallOutcomeItem,
        );
        state.defaultOutcomes = outcomes;
        state.defaultOutcomePagination.options.page =
          action.payload.payload.meta.currentPage;
        state.defaultOutcomePagination.count =
          action.payload.payload.meta.totalItems;
        state.isLoading = false;
      },
    );
    builder.addCase(
      getDefaultCallOutcomesRequest.rejected,
      (state, action) => {
        state.getDefaultCallOutcomesRequest.status = RequestStatus.Failed;
        state.getDefaultCallOutcomesRequest.error = action.payload;
        state.isLoading = false;
      },
    );

    // Get all call outcomes (for delete modal dropdown)
    builder.addCase(getAllCallOutcomesRequest.pending, (state) => {
      state.getAllCallOutcomesRequest.status = RequestStatus.Pending;
      state.getAllCallOutcomesRequest.error = null;
    });
    builder.addCase(
      getAllCallOutcomesRequest.fulfilled,
      (state, action) => {
        state.getAllCallOutcomesRequest.status = RequestStatus.Succeeded;
        state.getAllCallOutcomesRequest.message = action.payload.message;
        // Without pagination, payload is an array directly
        const items = Array.isArray(action.payload.payload)
          ? action.payload.payload
          : action.payload.payload.items || [];
        state.allOutcomes = items.map(processCallOutcomeItem);
      },
    );
    builder.addCase(
      getAllCallOutcomesRequest.rejected,
      (state, action) => {
        state.getAllCallOutcomesRequest.status = RequestStatus.Failed;
        state.getAllCallOutcomesRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Create call outcome
    builder.addCase(createCallOutcomeRequest.pending, (state) => {
      state.createCallOutcomeRequest.status = RequestStatus.Pending;
      state.createCallOutcomeRequest.error = null;
      state.isLoading = true;
    });
    builder.addCase(
      createCallOutcomeRequest.fulfilled,
      (state, action) => {
        state.createCallOutcomeRequest.status = RequestStatus.Succeeded;
        state.createCallOutcomeRequest.message = action.payload.message;
        state.isLoading = false;
      },
    );
    builder.addCase(
      createCallOutcomeRequest.rejected,
      (state, action) => {
        state.createCallOutcomeRequest.status = RequestStatus.Failed;
        state.createCallOutcomeRequest.error =
          !action.payload.isHandled && action.payload;
        state.isLoading = false;
      },
    );

    // Update call outcome
    builder.addCase(updateCallOutcomeRequest.pending, (state) => {
      state.updateCallOutcomeRequest.status = RequestStatus.Pending;
      state.updateCallOutcomeRequest.error = null;
      state.isLoading = true;
    });
    builder.addCase(
      updateCallOutcomeRequest.fulfilled,
      (state, action) => {
        state.updateCallOutcomeRequest.status = RequestStatus.Succeeded;
        state.updateCallOutcomeRequest.message = action.payload.message;
        state.isLoading = false;
      },
    );
    builder.addCase(
      updateCallOutcomeRequest.rejected,
      (state, action) => {
        state.updateCallOutcomeRequest.status = RequestStatus.Failed;
        state.updateCallOutcomeRequest.error =
          !action.payload.isHandled && action.payload;
        state.isLoading = false;
      },
    );

    // Delete call outcome
    builder.addCase(deleteCallOutcomeRequest.pending, (state) => {
      state.deleteCallOutcomeRequest.status = RequestStatus.Pending;
      state.deleteCallOutcomeRequest.error = null;
      state.isLoading = true;
    });
    builder.addCase(
      deleteCallOutcomeRequest.fulfilled,
      (state, action) => {
        state.deleteCallOutcomeRequest.status = RequestStatus.Succeeded;
        state.deleteCallOutcomeRequest.message = action.payload.message;
        state.isLoading = false;
      },
    );
    builder.addCase(
      deleteCallOutcomeRequest.rejected,
      (state, action) => {
        state.deleteCallOutcomeRequest.status = RequestStatus.Failed;
        state.deleteCallOutcomeRequest.error =
          !action.payload.isHandled && action.payload;
        state.isLoading = false;
      },
    );
  },
});

export const { resetCallOutcomeData } = callOutcomeSlice.actions;
export default callOutcomeSlice.reducer;
